/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { App, Modal, Tabs } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { paragraphMedium, weightRegular } from 'src/components/css'
import Button from 'src/components/ts-components/Button'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { DivisionSetupContextWrapper, useDivisionSetupContext } from './context'
import GeneralSettings from './forms/GeneralSettings'
import Payments from './forms/Payments'
// import Waiver from './forms/Waiver'
// import CustomQuestionsForm from './forms/CustomQuestionsForm'
import {
  Currency,
  RegistrationTypes,
  Tax,
} from '@sportninja/common/types/Registration'
import { useModalScroll } from 'src/hooks/useModalScroll'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { RegistrationTableDivision } from '../PlayerRegistration/helpers'
import PlayerPayments from './forms/PlayerPayments'
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons'
import useMonitorResolution, {
  MonitorResolution,
} from 'src/hooks/useMonitorResolution'
import TaxesForm from './forms/TaxesForm'

const labelById = {
  0: 'General Settings',
  1: 'Team Payments',
  2: 'Player Payments',
  3: 'Taxes',
  // 2: 'Waiver',
  // 3: 'Custom Questions',
}
const styles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`
const classNames = {
  body: styles['my-modal-body'],
  mask: styles['my-modal-mask'],
  header: styles['my-modal-header'],
  footer: styles['my-modal-footer'],
  content: styles['my-modal-content'],
}
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
    maxHeight: 'calc(100vh - 50px)',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    // backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type DivisionSetupProps = {
  isFormOpen: boolean
  setIsFormOpen: (isFormOpen: boolean) => void
  division?: RegistrationTableDivision
  scheduleCurrency?: Currency
  types: RegistrationTypes
  refetch: (displayLoading?: boolean) => void
  setSelectedDivision: (division: RegistrationTableDivision | null) => void
  divisions: RegistrationTableDivision[]
  playerRegistrationEnabled: boolean
  teamRegistrationEnabled: boolean
  availableTaxes: Tax[]
}

const DivisionSetupContent = ({
  isFormOpen,
  setIsFormOpen,
  refetch,
  setSelectedDivision,
  division,
  divisions,
  playerRegistrationEnabled,
  teamRegistrationEnabled,
  availableTaxes,
}: DivisionSetupProps) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [activeTab, setActiveTab] = useState('0')
  const { submitDivisionRegistration, state, isUpdate, clearState } =
    useDivisionSetupContext()
  const { message } = App.useApp()
  const { openModal, closeModal } = useModalScroll()
  const monitorResolution = useMonitorResolution()
  const rightPosition = useMemo(() => {
    switch (monitorResolution) {
      case MonitorResolution.HD:
        return '15%'
      case MonitorResolution.FULL_HD:
        return '25%'
      case MonitorResolution.QHD:
        return '30%'
      case MonitorResolution.UHD_4K:
        return '38%'
      case MonitorResolution.UW_QHD:
        return '36%'
      case MonitorResolution.UW_WQHD:
        return '38%'
      case MonitorResolution.SUPER_UW:
        return '41%'
      case MonitorResolution.SXGA:
        return '15%'
      case MonitorResolution.MBP_14_LARGE:
        return '25%'
      case MonitorResolution.MBP_14_DEFAULT:
        return '20%'
      case MonitorResolution.MBP_14_MEDIUM:
        return '15%'
      case MonitorResolution.MBP_14_SMALL:
        return '10%'
      case MonitorResolution.MBP_14_SMALLEST:
        return '6%'
      default:
        return '15%'
    }
  }, [monitorResolution])

  const currentDivisionIndex = useMemo(() => {
    return divisions.findIndex((d) => d.id === division?.id)
  }, [divisions, division])

  const hasPrevDivision = useMemo(() => {
    return currentDivisionIndex > 0
  }, [currentDivisionIndex])

  const hasNextDivision = useMemo(() => {
    return currentDivisionIndex < divisions.length - 1
  }, [currentDivisionIndex, divisions])

  const handlePrevDivision = useCallback(() => {
    if (hasPrevDivision) {
      setSelectedDivision(divisions[currentDivisionIndex - 1])
    }
  }, [currentDivisionIndex, divisions, hasPrevDivision, setSelectedDivision])

  const handleNextDivision = useCallback(() => {
    if (hasNextDivision) {
      setSelectedDivision(divisions[currentDivisionIndex + 1])
    }
  }, [currentDivisionIndex, divisions, hasNextDivision, setSelectedDivision])

  // const onClose = useCallback(() => {
  //   clearState()
  //   setActiveTab('0')
  //   setIsFormOpen(false)
  //   refetch()
  // }, [clearState, setActiveTab, setIsFormOpen, refetch])

  useEffect(() => {
    if (isFormOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [closeModal, isFormOpen, openModal])

  const getFormById = (id) => {
    switch (id) {
      case '0':
        return <GeneralSettings />
      case '1':
        return <Payments />
      case '2':
        return <PlayerPayments />
      case '3':
        return <TaxesForm />
      // case '2':
      //   return <Waiver />
      // case '3':
      //   return <CustomQuestionsForm />
      default:
        return null
    }
  }

  const getFooter = useCallback(() => {
    return (
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: 16px;
          width: ${isMobile ? '100%' : '50%'};
          margin-left: ${isMobile ? '0' : 'auto'};
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column;
          }
        `}
      >
        <Button
          text='Cancel'
          onClick={() => {
            clearState()
            setActiveTab('0')
            setIsFormOpen(false)
          }}
          variant='secondary'
          disabled={state.loading}
        />
        <Button
          text='Save'
          onClick={async () => {
            try {
              await submitDivisionRegistration()
              message.success(
                isUpdate
                  ? `${state.divisionName} Updated Successfully`
                  : `${state.divisionName} Saved Successfully`
              )
              await refetch(false)
              setActiveTab('0')
              setIsFormOpen(false)
              setSelectedDivision(null)
            } catch (error: any) {
              if (error?.invalid_fields) {
                if (error.invalid_fields.maxPlayersPerTeam) {
                  setActiveTab('0')
                  return
                }
                if (error.invalid_fields.maxTeams) {
                  setActiveTab('0')
                  return
                }
                if (error.invalid_fields.installments) {
                  setActiveTab('1')
                  return
                }
                if (error.invalid_fields.installment_frequency) {
                  setActiveTab('1')
                  return
                }
                if (error.invalid_fields.player_installments) {
                  setActiveTab('2')
                  return
                }
                if (error.invalid_fields.deposit) {
                  setActiveTab('1')
                  return
                }
                if (error.invalid_fields.costPerRegistration) {
                  setActiveTab('1')
                  return
                }
                if (error.invalid_fields.playerCostPerRegistration) {
                  setActiveTab('2')
                  return
                }
                if (error.invalid_fields.playerDeposit) {
                  setActiveTab('2')
                  return
                }
                return
              }
              message.error(
                error.message
                  ? error.message
                  : 'Failed to Save Division Settings'
              )
            }
          }}
          variant='primary'
          disabled={state.loading}
        />
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.loading,
    setIsFormOpen,
    submitDivisionRegistration,
    message,
    refetch,
    isMobile,
  ])

  const getHeader = useCallback(() => {
    return (
      <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
        Division Settings
      </Text>
    )
  }, [])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
          cursor: ${state.loading ? 'not-allowed' : 'pointer'};
          opacity: ${state.loading ? 0.5 : 1};
        `}
      />
    )
  }, [state.loading])

  const tabItems = useMemo(() => {
    const items = [
      {
        label: labelById[0],
        key: '0',
        children: getFormById('0'),
        disabled: false,
      },
    ]

    if (teamRegistrationEnabled) {
      items.push({
        label: labelById[1],
        key: '1',
        children: getFormById('1'),
        disabled: false,
      })
    }

    if (playerRegistrationEnabled) {
      items.push({
        label: labelById[2],
        key: '2',
        children: getFormById('2'),
        disabled: false,
      })
    }

    if (availableTaxes.length > 0) {
      items.push({
        label: labelById[3],
        key: '3',
        children: getFormById('3'),
        disabled: false,
      })
    }

    return items
  }, [teamRegistrationEnabled, playerRegistrationEnabled, availableTaxes])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        background: ${colors.SECONDARY_400} !important;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '770px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }
        .ant-modal-body {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
        .ant-tabs-tabpane {
          background: ${colors.SECONDARY_300} !important;
          overflow-y: auto;
        }
        .ant-tabs-nav {
          min-height: ${isMobile ? 'auto' : '600px'} !important;
        }
        .ant-tabs-tab {
          height: 40px !important;
        }
        /* if the tab is active, change the background color */
        .ant-tabs-tab-active {
          background-color: #26303e;
        }
        .ant-tabs-tab-btn {
          ${paragraphMedium}
          ${weightRegular}
        }
        .ant-tabs-tab-btn[aria-selected='true'] {
          color: ${colors.PRIMARY}!important;
        }
        .ant-tabs-tab-btn[aria-selected='false'] {
          color: ${colors.NEUTRAL_30}!important;
        }
        .ant-tabs-tab.ant-tabs-tab-disabled {
          color: ${colors.NEUTRAL_30}!important;
          cursor: not-allowed !important;
          opacity: 0.5 !important;
        }
      `}
      width={isMobile ? '100%' : 770}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isFormOpen}
      onOk={() => setIsFormOpen(false)}
      onCancel={() => {
        clearState()
        setActiveTab('0')
        setIsFormOpen(false)
      }}
      onClose={() => {
        clearState()
        setActiveTab('0')
        setIsFormOpen(false)
      }}
      centered={!isMobile}
      classNames={classNames}
      styles={modalStyles}
      maskClosable={false}
    >
      <Tabs
        activeKey={activeTab}
        defaultActiveKey='0'
        onChange={(key) => setActiveTab(key)}
        tabPosition={isMobile ? 'top' : 'left'}
        css={css`
          display: flex;
          flex: 1;
          height: 100%;
          overflow: hidden;

          .ant-tabs-nav {
            @media (max-width: 768px) {
              margin-bottom: 24px;
              width: 100%;
              min-height: auto !important;
              border-bottom: 1px solid ${colors.SOFT_STEEL};

              .ant-tabs-nav-list {
                width: 100%;
                display: flex;
              }

              .ant-tabs-nav-operations {
                display: none !important;
              }
            }
          }

          .ant-tabs-content {
            height: 100%;
          }

          .ant-tabs-content-holder {
            flex: 1;
            overflow: hidden;
          }

          .ant-tabs-tab {
            height: 40px !important;
            @media (max-width: 768px) {
              margin: 0 !important;
              padding: 8px 16px !important;
              flex: 1;
              justify-content: center;
              min-width: 120px;
            }
          }

          .ant-tabs-tab-active {
            background-color: #26303e;
            @media (max-width: 768px) {
              border-bottom: 2px solid ${colors.PRIMARY} !important;
            }
          }

          .ant-tabs-tab-btn {
            ${paragraphMedium}
            ${weightRegular}
            @media (max-width: 768px) {
              text-align: center;
              width: 100%;
              white-space: nowrap;
            }
          }

          .ant-tabs-tab-btn[aria-selected='true'] {
            color: ${colors.PRIMARY}!important;
          }

          .ant-tabs-tab-btn[aria-selected='false'] {
            color: ${colors.NEUTRAL_30}!important;
          }

          .ant-tabs-content-holder {
            @media (max-width: 768px) {
              padding: 0 16px;
              max-height: 100%;
              overflow-y: auto;
              height: 100%;
            }
          }

          .ant-tabs-ink-bar {
            @media (max-width: 768px) {
              display: none;
            }
          }
        `}
        items={tabItems}
        className='tab-options'
      />
      <div
        css={css`
          position: fixed;
          top: 50%;
          right: ${rightPosition};
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 10002;
          @media (max-width: 768px) {
            display: none;
          }
          // hide this until I find a way to fix it
          display: none;
        `}
      >
        <div
          css={css`
            width: 40px;
            height: 40px;
            border-radius: 20px;
            background: ${hasPrevDivision
              ? colors.PRIMARY
              : colors.NEUTRAL_100};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: ${hasPrevDivision ? 'pointer' : 'not-allowed'};
            &:hover {
              opacity: ${hasPrevDivision ? 0.8 : 1};
            }
          `}
          onClick={hasPrevDivision ? handlePrevDivision : undefined}
        >
          <ArrowUpOutlined
            css={css`
              color: ${colors.WHITE};
              font-size: 20px;
            `}
          />
        </div>
        <div
          css={css`
            width: 40px;
            height: 40px;
            border-radius: 20px;
            background: ${hasNextDivision
              ? colors.PRIMARY
              : colors.NEUTRAL_100};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: ${hasNextDivision ? 'pointer' : 'not-allowed'};
            &:hover {
              opacity: ${hasNextDivision ? 0.8 : 1};
            }
          `}
          onClick={hasNextDivision ? handleNextDivision : undefined}
        >
          <ArrowDownOutlined
            css={css`
              color: ${colors.WHITE};
              font-size: 20px;
            `}
          />
        </div>
      </div>
    </Modal>
  )
}

const DivisionSetup = (props: DivisionSetupProps) => {
  return (
    <DivisionSetupContextWrapper
      division={props.division}
      scheduleCurrency={props.scheduleCurrency}
      types={props.types}
      setSelectedDivision={props.setSelectedDivision}
      playerRegistrationEnabled={props.playerRegistrationEnabled}
      teamRegistrationEnabled={props.teamRegistrationEnabled}
      availableTaxes={props.availableTaxes}
    >
      <DivisionSetupContent {...props} />
    </DivisionSetupContextWrapper>
  )
}

export default DivisionSetup
